import { useCallback, useContext, useMemo } from 'react';
import { useInventoryEventBulkActionsState } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryEventBulkActions/useInventoryEventBulkActionsState';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import {
  useSetNewGroupId,
  useSetOpenAutoPricingForGroupId,
} from 'src/contexts/NewGroupScrollContext/NewGroupScroll.atoms';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { Stack } from 'src/core/ui';
import { useListingGroupSellerAccountSettings } from 'src/hooks/api/useListingGroupSellerAccountSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { AddToGroupListingsModal } from 'src/modals/GroupListings';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { newGuidId } from 'src/utils/idUtils';
import { EventWithData, Feature, Listing } from 'src/WebApiController';

export const useBulkQuickGroup = (eventData: EventWithData) => {
  const { setModal } = useContext(ModalContext);

  const hasListingGroupAccountDefaultsFeature = useUserHasFeature(
    Feature.ListingGroupAccountDefaults
  );
  const hasSelectGroupAfterCreationFeature = useUserHasFeature(
    Feature.SelectGroupAfterCreation
  );
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const { data: accountDefaults } = useListingGroupSellerAccountSettings();

  const setNewGroupId = useSetNewGroupId();
  const setOpenAutoPricingForGroupId = useSetOpenAutoPricingForGroupId();

  const { filterQueryWithEventIds, isAllListingsSelected } =
    useInventoryEventBulkActionsState(eventData.event);

  const eventListings = useMemo(() => {
    return (
      (eventData?.entities?.listings?.flatMap(
        flattenListingGroup
      ) as Listing[]) ?? []
    );
  }, [eventData?.entities?.listings]);

  const selectedListingIds = useMemo(() => {
    if (isAllListingsSelected) {
      return filterQueryWithEventIds.entityIds?.length
        ? filterQueryWithEventIds.entityIds
        : eventListings.map((listing) => listing.id);
    }
    return filterQueryWithEventIds.entityIds ?? [];
  }, [eventListings, filterQueryWithEventIds.entityIds, isAllListingsSelected]);

  const onGroupCreatedCallback = useCallback(
    (groupId: string) => {
      if (hasSelectGroupAfterCreationFeature) {
        setNewGroupId(groupId);
        if (hasAutoPricingFeature) {
          setOpenAutoPricingForGroupId(groupId);
        }
      }
    },
    [
      hasAutoPricingFeature,
      hasSelectGroupAfterCreationFeature,
      setNewGroupId,
      setOpenAutoPricingForGroupId,
    ]
  );

  const onQuickGroup = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (selectedListingIds.length === 0) return;

      const firstListingId = selectedListingIds[0];
      const firstListing = eventListings.find((l) => l.id === firstListingId);

      const groupName = firstListing
        ? `${[firstListing.seating.section, firstListing.seating.row].filter(Boolean).join(' ')} x${firstListing.unsoldQty}`.trim()
        : '';

      const newGroupId = newGuidId();

      const accountDesiredActiveListings =
        accountDefaults?.desiredActiveListings != null
          ? accountDefaults.desiredActiveListings
          : undefined;

      const accountPricingSettingsJson =
        accountDefaults?.pricingSettingsJson ?? undefined;

      const accountDeprioritizedQuantities =
        accountDefaults?.deprioritizedQuantities ?? undefined;

      const accountDefaultSortId = accountDefaults?.defaultSortId ?? undefined;

      setModal({
        children: (
          <AddToGroupListingsModal
            selectedListingIds={selectedListingIds}
            listingGroupId={newGroupId}
            viagVirtId={eventData.event.virtId}
            newListingGroupName={groupName}
            inheritListingGroupSettings={true}
            defaultDesiredActiveListings={accountDesiredActiveListings}
            defaultPricingSettingsJson={accountPricingSettingsJson}
            defaultDeprioritizedQuantities={accountDeprioritizedQuantities}
            defaultSortId={accountDefaultSortId}
            onGroupCreated={onGroupCreatedCallback}
          />
        ),
        clickOutsideToClose: true,
        size: 'slide-in',
      });
    },
    [
      accountDefaults,
      eventData.event.virtId,
      eventListings,
      onGroupCreatedCallback,
      selectedListingIds,
      setModal,
    ]
  );

  const actionItems = useMemo(() => {
    if (!hasListingGroupAccountDefaultsFeature) {
      return [];
    }
    return [{ title: ContentId.QuickGroup, onClick: onQuickGroup }];
  }, [hasListingGroupAccountDefaultsFeature, onQuickGroup]);

  if (!hasListingGroupAccountDefaultsFeature) {
    return { dropdown: null, actionItems: [] };
  }

  return {
    dropdown: (
      <PosDropdownItem key="QuickGroup" onClick={onQuickGroup}>
        <Stack alignItems="center" gap="m" width="full">
          <Content id={ContentId.QuickGroup} />
        </Stack>
      </PosDropdownItem>
    ),
    actionItems,
  };
};
